import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import AppBar from "../components/layout/AppBar";
import Toolbar from "../components/matrix/Toolbar";
import MatrixGrid from "../components/matrix/MatrixGrid";
import { NewMapDialog, LoadMapDialog } from "../components/matrix/FeatureMapDialogs";
import LookupSheet from "../components/matrix/LookupSheet";
import CostSheet from "../components/matrix/CostSheet";
import { api, errorMessage, getClientId } from "../lib/api";
import { sortByOrder, formatPrice } from "../lib/matrix";
import { localizePlan, localizeAddon, regionMeta, readRegion, LS_REGION } from "../lib/pricing";
import { computeCost, readSeats, serializeSeats, LS_SEATS } from "../lib/cost";
import { buildCSV, buildHTML, downloadBlob } from "../utils/exporters";

const LS_SELECTION = "m365matrix.selection";
const LS_PRICE_MODE = "m365matrix.priceMode";
const LS_FILTERS_OPEN = "m365matrix.filtersOpen";

const readJSON = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const readPinned = () => {
  const raw = new URLSearchParams(window.location.search).get("compare");
  return raw ? raw.split("|").map((s) => s.trim()).filter(Boolean).slice(0, 3) : [];
};

const filterRows = (rows, { search, diffOnly, visiblePlans, enabledIds }) => {
  const q = search.trim().toLowerCase();
  const scope = visiblePlans.length ? visiblePlans : enabledIds;
  const features = rows.filter((row) => {
    if (row.type !== "feature") return true;
    // Hide rows that have no value for any shown license (e.g. Education-only features while EDU columns are hidden)
    if (scope.length && !scope.some((id) => row.cells?.[id])) return false;
    if (q && !row.name.toLowerCase().includes(q)) return false;
    if (diffOnly && visiblePlans.length > 1) {
      const first = row.cells?.[visiblePlans[0]] || "";
      if (visiblePlans.every((id) => (row.cells?.[id] || "") === first)) return false;
    }
    return true;
  });
  // Drop section / sub-group headers that no longer have any feature rows beneath them.
  const out = [];
  for (let i = 0; i < features.length; i += 1) {
    const row = features[i];
    if (row.type === "feature") {
      out.push(row);
      continue;
    }
    let hasFeature = false;
    for (let j = i + 1; j < features.length; j += 1) {
      const next = features[j];
      if (next.type === "section" || (row.type === "sub" && next.type === "sub")) break;
      if (next.type === "feature") {
        hasFeature = true;
        break;
      }
    }
    if (hasFeature) out.push(row);
  }
  return out;
};

export default function MatrixPage() {
  const [dataset, setDataset] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [visiblePlans, setVisiblePlans] = useState(null);
  const [search, setSearch] = useState("");
  const [diffOnly, setDiffOnly] = useState(false);
  const [timeline, setTimeline] = useState(false);
  const [showPrices, setShowPrices] = useState(true);
  const [priceMode, setPriceMode] = useState(() => readJSON(LS_PRICE_MODE, "teams"));
  const [region, setRegion] = useState("us");
  const [filtersOpen, setFiltersOpen] = useState(() => readJSON(LS_FILTERS_OPEN, true));
  const [featureMap, setFeatureMap] = useState(null);
  const [savedMaps, setSavedMaps] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.has("lookup") || p.has("compare");
  });
  const [lookupFeature, setLookupFeature] = useState(() => new URLSearchParams(window.location.search).get("lookup") || "");
  const [pinned, setPinned] = useState(readPinned);
  const [lookupMode, setLookupMode] = useState(() => (readPinned().length >= 2 ? "compare" : "single"));

  const openLookup = useCallback((name) => {
    if (typeof name === "string") {
      setLookupFeature(name);
      setLookupMode("single");
    }
    setLookupOpen(true);
  }, []);

  // Keep ?lookup= / ?compare= in the URL in sync so lookups are shareable
  useEffect(() => {
    const url = new URL(window.location.href);
    if (lookupOpen && lookupFeature) url.searchParams.set("lookup", lookupFeature);
    else url.searchParams.delete("lookup");
    if (lookupOpen && pinned.length) url.searchParams.set("compare", pinned.join("|"));
    else url.searchParams.delete("compare");
    window.history.replaceState({}, "", url.toString());
  }, [lookupOpen, lookupFeature, pinned]);

  useEffect(() => {
    if (pinned.length < 2 && lookupMode === "compare") setLookupMode("single");
  }, [pinned, lookupMode]);

  // Cost calculator: seat counts per license (persisted per browser, shareable via ?seats=)
  const [costOpen, setCostOpen] = useState(() => new URLSearchParams(window.location.search).has("seats"));
  const [seats, setSeats] = useState(readSeats);
  useEffect(() => {
    const serialized = serializeSeats(seats);
    window.localStorage.setItem(LS_SEATS, serialized);
    const url = new URL(window.location.href);
    if (serialized && costOpen) url.searchParams.set("seats", serialized);
    else url.searchParams.delete("seats");
    window.history.replaceState({}, "", url.toString());
  }, [seats, costOpen]);

  // Ctrl/Cmd + K opens the lookup
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setLookupOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const clientId = useMemo(() => getClientId(), []);

  const groups = useMemo(() => sortByOrder(dataset?.groups || []), [dataset]);
  const plans = useMemo(() => sortByOrder(dataset?.plans || []).map((p) => localizePlan(p, region)), [dataset, region]);
  const addons = useMemo(() => (dataset?.addons || []).map((a) => localizeAddon(a, region)), [dataset, region]);
  const pricing = useMemo(() => regionMeta(dataset, region), [dataset, region]);
  const costTotal = useMemo(() => {
    const c = computeCost({ seats, plans, addons, priceMode });
    return c.monthly > 0 ? formatPrice(c.monthly, pricing.symbol || "$", c.estimated) : "";
  }, [seats, plans, addons, priceMode, pricing]);
  const enabledPlans = useMemo(() => plans.filter((p) => p.enabled), [plans]);
  const enabledIds = useMemo(() => enabledPlans.map((p) => p.id), [enabledPlans]);
  // The lookup only considers license families that are switched on in the matrix (e.g. Education stays out until toggled).
  const lookupPlans = useMemo(() => {
    const shownGroups = new Set(enabledPlans.filter((p) => visiblePlans?.includes(p.id)).map((p) => p.group));
    if (!shownGroups.size) return plans;
    return plans.map((p) => (shownGroups.has(p.group) ? p : { ...p, enabled: false }));
  }, [plans, enabledPlans, visiblePlans]);

  const loadMatrix = useCallback(() => {
    setLoadError("");
    api
      .matrix()
      .then((data) => {
        setDataset(data);
        setRegion(readRegion(data));
      })
      .catch((err) => setLoadError(errorMessage(err, "Could not load the matrix")));
  }, []);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  // Initial column selection: shared link -> saved selection -> all enabled plans in default-visible groups
  useEffect(() => {
    if (!dataset || visiblePlans !== null) return;
    const params = new URLSearchParams(window.location.search);
    const shared = params.get("sel");
    const hiddenGroups = new Set(groups.filter((g) => g.defaultVisible === false).map((g) => g.id));
    const defaults = enabledPlans.filter((p) => !hiddenGroups.has(p.group)).map((p) => p.id);
    const apply = (ids) => {
      const filtered = (ids || []).filter((id) => enabledIds.includes(id));
      setVisiblePlans(filtered.length ? enabledIds.filter((id) => filtered.includes(id)) : defaults);
    };
    if (shared) {
      api.selections
        .get(shared)
        .then((sel) => {
          apply(sel.plans);
          toast.success(`Loaded shared selection "${sel.name}"`);
        })
        .catch(() => {
          toast.error("Shared selection not found - showing all licenses");
          apply(enabledIds);
        });
    } else {
      apply(readJSON(LS_SELECTION, null));
    }
  }, [dataset, enabledIds, enabledPlans, groups, visiblePlans]);

  useEffect(() => {
    window.localStorage.setItem(LS_PRICE_MODE, JSON.stringify(priceMode));
  }, [priceMode]);
  useEffect(() => {
    window.localStorage.setItem(LS_FILTERS_OPEN, JSON.stringify(filtersOpen));
  }, [filtersOpen]);

  const rows = useMemo(
    () =>
      dataset && visiblePlans ? filterRows(dataset.rows, { search, diffOnly, visiblePlans, enabledIds }) : [],
    [dataset, search, diffOnly, visiblePlans, enabledIds]
  );

  const totalFeatures = useMemo(
    () =>
      (dataset?.rows || []).filter(
        (r) => r.type === "feature" && (!enabledIds.length || enabledIds.some((id) => r.cells?.[id]))
      ).length,
    [dataset, enabledIds]
  );
  const shownFeatures = useMemo(() => rows.filter((r) => r.type === "feature").length, [rows]);

  const togglePlan = useCallback(
    (id) => {
      setVisiblePlans((prev) =>
        prev.includes(id) ? prev.filter((p) => p !== id) : enabledIds.filter((p) => p === id || prev.includes(p))
      );
    },
    [enabledIds]
  );

  const toggleGroup = useCallback(
    (groupId, on) => {
      const groupIds = enabledPlans.filter((p) => p.group === groupId).map((p) => p.id);
      setVisiblePlans((prev) =>
        on ? enabledIds.filter((p) => groupIds.includes(p) || prev.includes(p)) : prev.filter((p) => !groupIds.includes(p))
      );
    },
    [enabledPlans, enabledIds]
  );

  const saveSelection = async () => {
    if (!visiblePlans?.length) return toast.error("Select at least one license first");
    setSaving(true);
    try {
      window.localStorage.setItem(LS_SELECTION, JSON.stringify(visiblePlans));
      const sel = await api.selections.create({
        client_id: clientId,
        name: `Selection (${visiblePlans.length} licenses)`,
        plans: visiblePlans,
      });
      const url = new URL(window.location.href);
      url.searchParams.set("sel", sel.id);
      window.history.replaceState({}, "", url.toString());
      let copied = false;
      try {
        await navigator.clipboard.writeText(url.toString());
        copied = true;
      } catch {
        copied = false;
      }
      toast.success("Selection saved", {
        description: copied ? "Share link copied to clipboard." : `Share link: ${url.toString()}`,
      });
    } catch (err) {
      toast.error("Could not save selection", { description: errorMessage(err) });
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  const visiblePlanObjects = () => enabledPlans.filter((p) => visiblePlans.includes(p.id));

  const handleDownload = () => {
    if (!visiblePlans?.length) return toast.error("Select at least one license first");
    downloadBlob(buildHTML(rows, visiblePlanObjects(), priceMode, pricing), "m365-feature-matrix.html", "text/html");
    toast.success("Matrix downloaded as HTML");
    return undefined;
  };

  const handleExport = () => {
    if (!visiblePlans?.length) return toast.error("Select at least one license first");
    downloadBlob(
      buildCSV(rows, visiblePlanObjects(), priceMode, pricing),
      "m365-feature-matrix.csv",
      "text/csv;charset=utf-8"
    );
    toast.success("Matrix exported as CSV");
    return undefined;
  };

  // ---- Feature maps (server side, scoped to this browser's client id) ----
  const createMap = (name) => {
    setFeatureMap({ id: null, name, values: {} });
    setNewOpen(false);
    toast.success(`Feature map "${name}" created`, {
      description: "Click cells in the new column to mark required features, then Save.",
    });
  };

  const toggleMapCell = (rowId) => {
    setFeatureMap((prev) => {
      if (!prev) return prev;
      const values = { ...prev.values };
      if (values[rowId]) delete values[rowId];
      else values[rowId] = true;
      return { ...prev, values };
    });
  };

  const saveMap = async () => {
    if (!featureMap) return;
    try {
      const payload = { name: featureMap.name.trim() || "My License Map", values: featureMap.values };
      const saved = featureMap.id
        ? await api.maps.update(featureMap.id, payload)
        : await api.maps.create({ client_id: clientId, ...payload });
      setFeatureMap({ id: saved.id, name: saved.name, values: saved.values });
      toast.success(`Feature map "${saved.name}" saved`);
    } catch (err) {
      toast.error("Could not save feature map", { description: errorMessage(err) });
    }
  };

  const openLoad = async () => {
    try {
      setSavedMaps(await api.maps.list(clientId));
      setLoadOpen(true);
    } catch (err) {
      toast.error("Could not load feature maps", { description: errorMessage(err) });
    }
  };

  const loadMap = (m) => {
    setFeatureMap({ id: m.id, name: m.name, values: { ...m.values } });
    setLoadOpen(false);
    toast.success(`Loaded "${m.name}"`);
  };

  const deleteMap = async (m) => {
    try {
      await api.maps.remove(m.id);
      setSavedMaps((prev) => prev.filter((x) => x.id !== m.id));
      if (featureMap?.id === m.id) setFeatureMap((prev) => (prev ? { ...prev, id: null } : prev));
      toast(`Deleted "${m.name}"`);
    } catch (err) {
      toast.error("Could not delete feature map", { description: errorMessage(err) });
    }
  };

  const handleRegion = (id) => {
    const found = (dataset?.regions || []).find((r) => r.id === id);
    if (!found) return;
    setRegion(id);
    window.localStorage.setItem(LS_REGION, id);
    toast.success(`Showing ${found.label} pricing (${found.currency})`, {
      description: id === "us" ? undefined : "Prices marked \u2248 are converted from the US list price where Microsoft publishes no local price.",
    });
  };

  const activeFilterCount = (search.trim() ? 1 : 0) + (diffOnly ? 1 : 0) + (timeline ? 1 : 0) + (featureMap ? 1 : 0);
  const ready = dataset && visiblePlans !== null;

  return (
    <div className="app-shell">
      <AppBar version={dataset?.version} updatedAt={dataset?.updated_at} />
      <main className="app-main">
        {loadError && (
          <div className="load-error glass" role="alert" data-testid="load-error">
            <span>Could not load the feature matrix: {loadError}</span>
            <button type="button" className="tb-btn sm" onClick={loadMatrix}>
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        )}

        {!ready && !loadError && (
          <div className="loading-state glass" data-testid="loading-state" aria-busy="true">
            <div className="skeleton" style={{ height: 44, width: "70%" }} />
            <div className="skeleton" style={{ height: 36, width: "50%" }} />
            <div className="skeleton" style={{ height: 320 }} />
            <p>Loading feature matrix...</p>
          </div>
        )}

        {ready && (
          <>
            <Toolbar
              groups={groups}
              plans={enabledPlans}
              visible={visiblePlans}
              onTogglePlan={togglePlan}
              onToggleGroup={toggleGroup}
              onSelectAll={() => setVisiblePlans([...enabledIds])}
              onSelectNone={() => setVisiblePlans([])}
              onSaveSelection={saveSelection}
              saving={saving}
              onDownload={handleDownload}
              onExport={handleExport}
              filtersOpen={filtersOpen}
              onToggleFilters={() => setFiltersOpen((v) => !v)}
              activeFilterCount={activeFilterCount}
              search={search}
              onSearchChange={setSearch}
              diffOnly={diffOnly}
              onToggleDiff={() => setDiffOnly((v) => !v)}
              timeline={timeline}
              onToggleTimeline={() => setTimeline((v) => !v)}
              showPrices={showPrices}
              onTogglePrices={() => setShowPrices((v) => !v)}
              priceMode={priceMode}
              onPriceMode={setPriceMode}
              region={region}
              regions={dataset.regions || []}
              onRegionChange={handleRegion}
              featureMap={featureMap}
              onMapNew={() => setNewOpen(true)}
              onMapLoad={openLoad}
              onMapSave={saveMap}
              onMapClose={() => {
                setFeatureMap(null);
                toast("Feature map closed");
              }}
              pricing={pricing}
              onOpenLookup={() => openLookup()}
              onOpenCost={() => setCostOpen(true)}
              costTotal={costTotal}
            />
            <section className="grid-card glass" aria-label="Feature matrix">
              <MatrixGrid
                rows={rows}
                groups={groups}
                plans={enabledPlans}
                visiblePlans={visiblePlans}
                pricing={pricing}
                search={search}
                priceMode={priceMode}
                showPrices={showPrices}
                timeline={timeline}
                featureMap={featureMap}
                onToggleMapCell={toggleMapCell}
                onMapNameChange={(name) => setFeatureMap((prev) => (prev ? { ...prev, name } : prev))}
                totalFeatures={totalFeatures}
                shownFeatures={shownFeatures}
                version={dataset.version}
                onLookup={openLookup}
              />
            </section>
          </>
        )}
      </main>

      <NewMapDialog open={newOpen} onOpenChange={setNewOpen} onCreate={createMap} />
      <LoadMapDialog open={loadOpen} onOpenChange={setLoadOpen} maps={savedMaps} onLoad={loadMap} onDelete={deleteMap} />
      {dataset && (
        <>
          <CostSheet
            open={costOpen}
            onOpenChange={setCostOpen}
            plans={plans}
            addons={addons}
            groups={groups}
            visiblePlans={visiblePlans || []}
            seats={seats}
            onSeatsChange={setSeats}
            priceMode={priceMode}
            pricing={pricing}
          />
          <LookupSheet
            open={lookupOpen}
            onOpenChange={setLookupOpen}
            dataset={dataset}
            plans={lookupPlans}
            addons={addons}
            groups={groups}
            priceMode={priceMode}
            pricing={pricing}
            feature={lookupFeature}
            onFeatureChange={setLookupFeature}
            pinned={pinned}
            onPinnedChange={setPinned}
            mode={lookupMode}
            onModeChange={setLookupMode}
            onShowInMatrix={(name) => {
              setSearch(name);
              setFiltersOpen(true);
              setLookupOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
}
